import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EmailField, PhoneField } from '../../components/contact-fields.js';
import { Button, Callout, Modal } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';

/**
 * A new supplier, from wherever you noticed they were missing (SCOPE §6).
 *
 * Vendors could only arrive through the seed, so a shop that picked up a new
 * brand could neither import their file nor write them an order without a
 * database edit — and both flows *begin* by choosing a vendor, which is exactly
 * where you find out they are not there. So the remedy sits inside both pickers
 * rather than on a settings screen somebody has to go and find (§2.2-11).
 *
 * One required field. The vendor code is the first segment of every SKU their
 * goods get (§18.1-3) and cannot change once anything is ordered, so it is
 * derived from the name and de-duplicated on the server rather than asked for —
 * a buyer inventing codes under time pressure is how codes stop meaning
 * anything. It is offered under *More* for the shop that already has a scheme.
 */
export function AddVendorDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** Chosen straight away — you are here because you were picking one. */
  onCreated: (vendor: { id: string; name: string }) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [showMore, setShowMore] = useState(false);

  const blank = (value: string) => (value.trim() === '' ? null : value.trim());

  const create = useMutation({
    mutationFn: () =>
      api.purchasing.createVendor.mutate({
        name: name.trim(),
        ...(code.trim() === '' ? {} : { code: code.trim() }),
        contactName: blank(contactName),
        email: blank(email),
        phone: blank(phone),
        // The header block item 25 prints — asked here so a vendor created
        // mid-import is not a vendor with no address on their first order.
        addressLine1: blank(addressLine1),
        city: blank(city),
        region: blank(region),
        postalCode: blank(postalCode),
      }),
    onSuccess: (vendor) => {
      recordBreadcrumb('purchasing', `added vendor ${vendor.name}`);
      // Both pickers read their own list, and both must see this immediately.
      void queryClient.invalidateQueries({ queryKey: ['import', 'setup'] });
      void queryClient.invalidateQueries({ queryKey: ['products', 'taxonomy'] });
      onCreated({ id: vendor.id, name: vendor.name });
    },
  });

  return (
    <Modal
      open
      title="Add a vendor"
      description="Enough to order from them. The rest can wait until you need it."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={create.isPending}
          disabledReason={name.trim().length < 2 ? 'Enter the vendor’s name' : undefined}
          onClick={() => create.mutate()}
        >
          Add {name.trim() === '' ? 'vendor' : name.trim()}
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="vendor-name">
          Name
        </label>
        <input
          id="vendor-name"
          className="input"
          autoFocus
          maxLength={120}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>

      {/* No domain suggestions: a rep is not statistically at gmail, and four
          wrong ones under every field teaches people to ignore the right ones. */}
      <EmailField
        label="Email"
        hint="Where their orders are sent"
        value={email}
        onChange={setEmail}
        suggestDomains={false}
      />

      {showMore ? (
        <>
          <div className="field">
            <label className="field__label" htmlFor="vendor-contact">
              Contact
            </label>
            <input
              id="vendor-contact"
              className="input"
              maxLength={120}
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
            />
          </div>

          {/* Punctuated like every other phone in the product — this is the
              number somebody rings when a delivery is short. */}
          <PhoneField value={phone} onChange={setPhone} />

          <div className="field">
            <label className="field__label" htmlFor="vendor-address">
              Street address
            </label>
            <input
              id="vendor-address"
              className="input"
              maxLength={200}
              value={addressLine1}
              onChange={(event) => setAddressLine1(event.target.value)}
            />
          </div>
          <div className="style-form">
            <div className="field">
              <label className="field__label" htmlFor="vendor-city">
                City
              </label>
              <input
                id="vendor-city"
                className="input"
                maxLength={100}
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="vendor-region">
                State
              </label>
              <input
                id="vendor-region"
                className="input"
                maxLength={100}
                value={region}
                onChange={(event) => setRegion(event.target.value)}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="vendor-postal">
                ZIP
              </label>
              <input
                id="vendor-postal"
                className="input"
                maxLength={20}
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-code">
              Code
            </label>
            <input
              id="vendor-code"
              className="input code"
              maxLength={12}
              placeholder="Worked out from the name"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
            />
            <p className="field__hint">
              The first part of every SKU their goods get. It cannot be changed once you have
              ordered from them, so leaving it blank is usually right.
            </p>
          </div>
        </>
      ) : (
        <Button variant="ghost" onClick={() => setShowMore(true)}>
          Contact, phone, address and SKU code
        </Button>
      )}

      {create.isError ? <Callout tone="danger">{errorMessage(create.error)}</Callout> : null}
    </Modal>
  );
}
